import fp from 'lodash/fp';
import { connect } from 'react-redux';
import container from 'src/ServiceContainer';
import { ASSORTMENT } from 'src/utils/Domain/Constants';
import { makePrintSensitive } from 'src/components/higherOrder/Print/PrintSenstive';
import {
  overloadSubheader,
  updateCompanionSortField,
  updateCompanionSortDirection,
  updateCompanionLevelByField,
} from 'src/components/Subheader/Subheader.slice';
import {
  cleanUp,
  receiveError,
  requestFlowSheetByStyleConfig,
  receiveFlowSheetByStyleConfig,
  fetchCompanionData,
  updateSelectedItem,
  refreshOvertimeData,
  receiveSomeFlowSheetConfig,
  submitMassEditPayload,
  FlowSheetViewDefns,
  fetchMassEditData,
} from './FlowSheetByStyle.slice';
import type { AppThunkDispatch } from 'src/store.types';
import type { AppState } from 'src/reducers';
import {
  StateSelection,
  projectState,
  createFilteredViewDefnsSelector,
  ViewDefns,
} from 'src/components/ListGridPair/ListGridPair.selectors';
import { FlowSheetByStyle } from 'src/pages/AssortmentBuild/FlowSheet/FlowSheetByStyle';
import { isNil, isEmpty } from 'lodash/fp';
import { get } from 'lodash';
import { makePopoverSensitive } from 'src/components/AssortmentStyleDetailsPopover/AssortmentStyleDetailsPopover';
import { MassEditConfig } from 'src/components/MassEdit/MassEdit';
import { FabType, getfabProps, withFab } from 'src/components/higherOrder/withFab';
import { updateAssortmentPlan } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.actions';
import { FlowSheetAndPricingDispatchProps, OwnProps, FlowSheetAndPricingMapState } from './FlowSheet.types';
import { SortByDirection } from 'src/components/Subheader/Subheader.types';
import { isDataLoaded } from 'src/services/pivotServiceCache';
import { ViewDataState } from 'src/types/Domain';
import { ComponentErrorType } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { AdornmentType } from 'configuration-schemas/viewdefns/literals';
import { zFlowSheetGridViewDefn, zMassEditDefn } from 'configuration-schemas/viewdefns/viewdefn';
import {
  zCompanionViewDefn,
  zGroupByConfig,
  zRollupDefn,
  zSortByConfig,
} from 'configuration-schemas/viewdefns/general';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { getLocalConfig } from 'src/components/Subheader/Favorites/Favorites.utils';
import { GranularEditPayloadItem } from 'src/dao/pivotClient';
import { bindActionCreators } from '@reduxjs/toolkit';
import { toggleCompanionCollapse } from 'src/state/uipose/UiPose.slice';
import {
  selectFlowSheetViewDataStates,
  selectFlowSheetHasMassEditViewDefn,
  selectFlowSheetHasMassEditModel,
  selectFlowSheetMassEditDataLoaded,
  selectFlowSheetCompanionData,
  selectFlowSheetOverTimeData,
} from './FlowSheetByStyle.selectors';

export function dispatchToProps(dispatch: AppThunkDispatch, ownProps: OwnProps): FlowSheetAndPricingDispatchProps {
  const { tenantConfigClient } = container;
  const { defns } = ownProps;
  const factory = {
    ...bindActionCreators({ onToggleCompanion: toggleCompanionCollapse }, dispatch),
    submitPayload(payload: GranularEditPayloadItem[]) {
      return dispatch(submitMassEditPayload(payload));
    },
    getMassEditData() {
      // do nothing
    },
    onCompanionItemClick(memberId: string) {
      dispatch(updateSelectedItem(memberId));
    },
    onShowView() {
      dispatch(requestFlowSheetByStyleConfig());
      tenantConfigClient
        .getTenantViewDefnsWithFavorites({
          defnIds: defns.view,
          appName: ASSORTMENT,
          validationSchemas: [
            zFlowSheetGridViewDefn,
            zSortByConfig,
            zGroupByConfig,
            zRollupDefn,
            zCompanionViewDefn,
            zMassEditDefn,
          ],
        })
        .then((resp) => {
          const defnStr = defns.view[0];
          const favoritesList = (resp as any)[defns.view.length];
          const unmodifiedViewDefn = resp[0];
          const listConfig = resp[4];
          const localConfig = getLocalConfig(defnStr, favoritesList, dispatch, unmodifiedViewDefn);

          const configWithFavorites =
            isNil(localConfig) || isNil(localConfig.config) ? unmodifiedViewDefn : localConfig.config;

          // if available, use model provided in the companion view defn
          const companionModel = listConfig.model ? listConfig.model : defns.models[0];
          const massEditModel = defns.models[1];
          const hasMassEditModel = selectFlowSheetHasMassEditModel({} as AppState, massEditModel);
          const hasMassEditViewDefnConfigured = selectFlowSheetHasMassEditViewDefn({} as AppState, defns);
          const massEditConfig = resp[defns.view.length - 1];
          const massEdit = hasMassEditViewDefnConfigured
            ? (massEditConfig as unknown as MassEditConfig) // FIXME: revisit this type issue
            : undefined;

          dispatch(
            receiveFlowSheetByStyleConfig({
              // lazy change, types are insane
              grid: configWithFavorites as any,
              listSort: resp[1],
              subheaderRollUp: resp[3],
              list: listConfig,
              massEdit,
              unmodifiedViewDefn,
            }),
          );
          dispatch(
            overloadSubheader({
              groupByOptions: resp[2],
              showFlowStatus: true,
              showSearch: true,
              title: 'Grid View',
            }),
          );
          if (ownProps.hideCompanion) {
            dispatch(refreshOvertimeData());
          } else {
            if (hasMassEditModel) {
              dispatch(fetchMassEditData(massEditModel));
            }
            dispatch(fetchCompanionData(companionModel));
          }
        })
        .catch((error) => {
          dispatch(
            receiveError({
              type: ComponentErrorType.config,
              message: (error as Error)?.message,
              name: ConfDefnComponentType.flowSheet,
              stack: (error as Error)?.stack,
              issues: error,
              defnId: error.defnId,
            }),
          );
        });
    },
    onRefetchOvertimeData() {
      dispatch(refreshOvertimeData());
    },
    onDestroy() {
      dispatch(cleanUp());
    },
    onUpdateConfig(config: TenantConfigViewData) {
      dispatch(receiveSomeFlowSheetConfig(config as unknown as FlowSheetViewDefns));
    },
    updateAssortmentPlan: () => {
      dispatch(updateAssortmentPlan('PlanQueue'));
    },
    updateCompanionSortField(field: string) {
      dispatch(updateCompanionSortField(field));
    },
    updateCompanionSortDirection(dir: SortByDirection) {
      dispatch(updateCompanionSortDirection(dir));
    },
    updateCompanionLevelField(dataIndex: string) {
      dispatch(updateCompanionLevelByField({ dataIndex }));
    },
  };

  return factory;
}

// asserted here because the types are wild
const getRawViewDefns = (state: AppState) => state.pages.assortmentBuild.flowSheet.viewDefns as unknown as ViewDefns;
export const getFlowSheetFilteredViewDefns = createFilteredViewDefnsSelector(getRawViewDefns);

export function mapStateToProps(state: AppState, ownProps: OwnProps): FlowSheetAndPricingMapState {
  const viewState = state.pages.assortmentBuild.flowSheet;
  const filteredViewDefns = getFlowSheetFilteredViewDefns(state);
  const {
    title,
    defns,
    keys,
    showPopover,
    fabType = FabType.none,
    showFlowStatus,
    showUndoBtn,
    hideCompanion,
  } = ownProps;
  const { rangeList, daysRangeList, timeInfo } = state.scope;
  const allowFrom = !isEmpty(rangeList) ? rangeList[0].id : '';
  const allowTo = !isEmpty(rangeList) ? rangeList[rangeList.length - 1].id : '';
  const fabProps = getfabProps(state, fabType);

  const companionData = selectFlowSheetCompanionData(state);
  const overTimeData = selectFlowSheetOverTimeData(state);
  const companionDataLoaded = isDataLoaded(viewState.viewDataStateCompanion);
  const overTimeDataLoaded = isDataLoaded(viewState.viewDataStateOverTime);
  const massEditModel = defns.models[1];
  const massEditDataLoaded = selectFlowSheetMassEditDataLoaded(state, { defns, massEditModel });
  const liveDataReady = viewState.viewDataStateOverTime === ViewDataState.liveDataReady;
  const adornments: AdornmentType[] = get(viewState.viewDefns, 'grid.adornments', []);

  const selectedId = viewState.selectedItem;
  const rowHeight = viewState.viewDefns.grid.main?.rowHeight;
  const groupRowHeight = viewState.viewDefns.grid.main?.groupRowHeight;
  const columnWidth = viewState.viewDefns.grid.main?.columnWidth;
  const configLoaded = !viewState.isConfigLoading;
  const dataLoaded = companionDataLoaded;
  const scopeStart = get(state, 'scope.scope.start');
  const viewDataStates = selectFlowSheetViewDataStates(state, defns);

  const originalState = {
    title,
    identityField: keys.idProp,
    treeData: companionData,
    subheaderState: state.subheader,
    subheaderViewDefns: defns.subheader,
    shouldFilterFlowStatus: true,
    showPopover,
    allowFrom,
    allowTo,
    rangeList: daysRangeList,
    ...viewState,
    viewDefns: filteredViewDefns,
    viewDataStates,
    activeTab: state.perspective.activeTab,
    timeInfo,
    showFlowStatus: showFlowStatus === false ? false : true,
    adornments,
    selectedId,
    rowHeight,
    groupRowHeight,
    columnWidth,
    configLoaded,
    dataLoaded,
    showUndoBtn,
    fab: fabProps,
    isCompanionCollapsed: state.uiPose.isCompanionCollapsed,
    hideCompanion,
    companionSortField: state.subheader.companionSortField,
    companionSortDirection: state.subheader.companionSortDirection,
    companionLevelField: state.subheader.companionLevelField,
  };

  const stateProps = {
    ...originalState,
    ...projectState(originalState as unknown as StateSelection),
    ...viewState,
    viewDefns: filteredViewDefns, // this get's copied again here because we can't distinguish in the 3 above spreads which order is correct
    scopeStart,
    overTimeDataLoaded,
    massEditDataLoaded,
    liveDataReady,
    overTimeData,
    companionSortField: state.subheader.companionSortField,
    companionSortDirection: state.subheader.companionSortDirection,
    companionLevelField: state.subheader.companionLevelField,
  };
  return stateProps;
}
const sensitiveView = fp.flow(() => FlowSheetByStyle, withFab, makePrintSensitive, makePopoverSensitive)();

export default connect(mapStateToProps, dispatchToProps)(sensitiveView);
